import type { Instant, LocalDate } from "adl-gen/common";
import {
  type PurchaseRequestState,
  valuesPurchaseRequestState,
} from "adl-gen/ferovinum/app/db";
import { assertNever } from "assert-never";
import {
  BaseProgressStepper,
  type BaseProgressStepperProps,
  compactStepperStates,
} from "components/widgets/base-progress-stepper/base-progress-stepper";
import { useMemo } from "react";
import { toLondonZonedLocalDate } from "utils/date-utils";

function getProgressStepperLabelFromState(state: PurchaseRequestState) {
  switch (state) {
    case "new":
      return "Created";
    case "purchaserAccepted":
      return "Purchaser accepted";
    case "productionOrderComplete":
      return "Prod order complete";
    case "readyForCollection":
      return "Ready for collection";
    case "collected":
      return "Collected";
    case "delivered":
      return "Delivered";
    case "purchaserInvoiced":
      return "Purchaser invoiced";
    case "purchaserPaid":
      return "Purchaser paid";
    case "organisationPaid":
      return "Org paid";
    case "cancelled":
      return "Cancelled";
    case "purchaserRejected":
      return "Purchaser rejected";
    default:
      return "Invalid";
  }
}

function isPurchaseRequestProgressState(state: PurchaseRequestState) {
  switch (state) {
    case "new":
    case "purchaserAccepted":
    case "productionOrderComplete":
    case "readyForCollection":
    case "collected":
    case "delivered":
    case "purchaserInvoiced":
    case "purchaserPaid":
    case "organisationPaid":
      return true;
    case "purchaserRejected":
    case "cancelled":
      return false;
    default:
      assertNever(state);
  }
}

const DISPLAYABLE_STATES = valuesPurchaseRequestState.filter(
  isPurchaseRequestProgressState,
);

export interface PurchaseRequestProgressStepperProps {
  currentState: PurchaseRequestState;
  lastStateSet: Instant;
  upcomingStateDeadline?: LocalDate;
  compact?: boolean;
}

function makePurchaseRequestDisplayProps({
  currentState,
  lastStateSet,
  upcomingStateDeadline,
  compact,
}: PurchaseRequestProgressStepperProps): BaseProgressStepperProps<PurchaseRequestState> {
  const states = !isPurchaseRequestProgressState(currentState)
    ? [currentState]
    : compact
      ? compactStepperStates(DISPLAYABLE_STATES, currentState)
      : DISPLAYABLE_STATES;
  return {
    currentState: {
      code: currentState,
      setAt: toLondonZonedLocalDate(lastStateSet),
      deadline: upcomingStateDeadline ?? null,
    },
    displayStates: states.map((s) => ({
      code: s,
      label: getProgressStepperLabelFromState(s),
    })),
  };
}

export const PurchaseRequestProgressStepper = (
  props: PurchaseRequestProgressStepperProps,
) => {
  const displayProps = useMemo(
    () => makePurchaseRequestDisplayProps(props),
    [props],
  );
  return <BaseProgressStepper<PurchaseRequestState> {...displayProps} />;
};
